<script lang="ts">
  import { _ } from "svelte-i18n";
  import Container from "$lib/components/Container/Container.svelte";
  import {
    type FliteImage as FI,
    type VideoSource,
  } from "$lib/utils/propTypes";
  import Typography, {
    TypeComponent,
    TypeVariant,
  } from "$lib/components/Typography/Typography.svelte";
  import Video from "$lib/components/Video/Video.svelte";
  import {
    Picture,
    FliteImage,
    type ImageInputConfig,
  } from "@thisissoon/flitejs-frontend";
  import Link from "../Link/Link.svelte";
  import { ColourTheme, type Link as LinkType } from "$lib/gql/cmsgen/codegen";
  import inView from "$lib/utils/inView";
  import HeroMediaLink from "./HeroMediaLink.svelte";
  import { defaultImgConfig } from "./Hero.image-config";

  export let index: number = 0;
  export let title = "";
  export let ctas: { text: string; href: string; textLight: boolean }[];
  export let image: FI;
  export let videoSources: VideoSource[] = [];
  export let colourTheme: ColourTheme = ColourTheme.Light;
  export let isContentSlot = false;
  export let blockCta: LinkType;
  export let imageInputConfig: ImageInputConfig = defaultImgConfig;

  let videoShouldPlay = false;
  let autoPlay = true;
  let windowWidth: number;
  let mediaContainerLink: LinkType;

  $: if (blockCta?.href && !ctas?.[0]?.href && !ctas?.[1]?.href) {
    mediaContainerLink = blockCta;
  } else if (blockCta?.href && ctas?.[0]?.href && !ctas?.[1]?.href) {
    mediaContainerLink = ctas[0];
  } else if (blockCta?.href && ctas?.[0]?.href && ctas?.[1]?.href) {
    mediaContainerLink = null;
  }

  $: fontClasses =
    colourTheme === ColourTheme.Light || colourTheme === ColourTheme.Primary
      ? "text-surface"
      : "text-inverse-surface";

  $: linkFontClasses =
    colourTheme === ColourTheme.Light || colourTheme === ColourTheme.Primary
      ? "text-surface border-surface border-b  hover:text-on-surface-variant hover:border-on-surface-variant"
      : "text-inverse-surface border-inverse-surface border-b hover:text-on-surface-variant hover:border-on-surface-variant";
</script>

<style lang="postcss">
  .hero-image {
    :global(.flite),
    :global(.flite picture) {
      @apply absolute left-0 top-0 h-full w-full object-cover object-center;
    }

    :global(.flite img) {
      @apply h-full object-cover object-center;
    }
  }

  :global(.hero-media-link img),
  :global(.hero-media-link video) {
    @apply transition duration-300;
  }

  :global(.hero-media-link:hover img),
  :global(.hero-media-link:hover video) {
    @apply scale-[103%];
  }

  .content-slot {
    :global(.flite),
    :global(.flite picture) {
      @apply absolute left-0 top-0 h-full w-full object-contain object-center;
    }
  }

  :global(.hero-video .video-js) {
    @apply m-0 aspect-[3/5] h-full w-full bg-[#e6e6e9] p-0 xl:aspect-video;
  }

  :global(.hero-video-slot .video-js) {
    @apply m-0 aspect-[4/5] h-full w-full bg-[#e6e6e9] p-0 xl:aspect-[4/3];
  }
</style>

<svelte:window bind:innerWidth={windowWidth} />

<HeroMediaLink link={mediaContainerLink}>
  <div
    class="relative overflow-hidden pb-6 lg:pb-10"
    use:inView={{ threshold: 0 }}
    on:intersect={({ detail }) => {
      if (detail) {
        videoShouldPlay = true;
        autoPlay = true;
      } else {
        autoPlay = false;
        videoShouldPlay = false;
      }
    }}
  >
    {#if image?.uri}
      <div
        class="{isContentSlot
          ? 'content-slot'
          : 'hero-image'} absolute left-0 top-0 h-full w-full"
      >
        <Picture
          altText={image?.altText}
          {imageInputConfig}
          imageInput={FliteImage.fromURL(image?.uri)}
          useInView={index !== 0}
        />
      </div>
    {:else}
      {#each videoSources as videoSource}
        {#if windowWidth && windowWidth >= videoSource.minWidth && windowWidth < videoSource.maxWidth}
          <div
            class="{isContentSlot
              ? 'hero-video-slot'
              : 'hero-video'} absolute left-0 top-0 h-full w-full"
          >
            <Video
              poster={videoSource.poster}
              sources={[
                {
                  src: videoSource.src,
                  type: videoSource.type
                    ? videoSource.type
                    : "application/x-mpegURL",
                  minWidth: videoSource.minWidth,
                  maxWidth: videoSource.maxWidth,
                },
              ]}
              autoplay={autoPlay}
              controls={false}
              {videoShouldPlay}
              muted={true}
              loop={true}
              fluid={false}
              fill={true}
            />
          </div>
        {/if}
      {/each}
    {/if}
    <div class="relative h-full w-full overflow-hidden">
      <Container>
        <div
          class="grid {isContentSlot
            ? 'aspect-[4/5] xl:aspect-[4/3]'
            : 'aspect-[3/5] xl:aspect-video'} grid-cols-2 grid-rows-1 items-end md:grid-cols-12"
        >
          <div
            class={`col-span-2 flex flex-col gap-6 md:col-span-6 lg:col-span-4 lg:gap-10`}
          >
            {#if title}
              <Typography
                component={TypeComponent.h1}
                variant={TypeVariant.H2}
                classes={fontClasses}
              >
                {$_(title)}
              </Typography>
            {/if}

            {#if ctas && ctas.length > 0}
              <div class="flex flex-wrap gap-6">
                {#each ctas as cta}
                  {#if cta && cta.text && cta.href}
                    <Link
                      label={cta?.text}
                      href={cta?.href}
                      colorClass={linkFontClasses}
                      typeVariant={TypeVariant.H4}
                    />
                  {/if}
                {/each}
              </div>
            {/if}
          </div>
        </div>
      </Container>
    </div>
  </div>
</HeroMediaLink>
